import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { map, replace } from 'lodash'
import useEventBus from '@/composables/eventBus'
import useTithelySSO from '@/composables/sso'
import queryClient from '@/plugins/query'
import { useMe } from '@/queries/me'
import { QUERY_KEYS } from '@/constants/query'
import NAV_MAIN from '@/constants/nav/main'
import type { NavMainItem } from '@/types/nav/main'
import { filterNavPermissions } from '@/functions/nav'

const useNavMain = () => {
    const me = useMe()
    const { emit, on } = useEventBus()
    const { path } = useRoute()
    const { redirect } = useTithelySSO()

    const linkedPersonId = computed(() => me.data.value?.linkedPersonId ?? null)

    // Catch all emits
    on(`*`, async (type: string) => {
        switch (type) {
            case `clearNav`:
                emit(`cacheCleared`)
                await queryClient.refetchQueries({ queryKey: QUERY_KEYS.me, exact: true })
                break
            case `refreshNav`:
                await queryClient.refetchQueries({ queryKey: QUERY_KEYS.me, exact: true })
                break
            case `clearGroupsDetails`:
                await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.groups.details() })
                break
            case `breezeBundle:addUser`:
                await redirect(`admin/users`)
                break
            case `breezeBundle:manageAccount`:
                await redirect(`admin/organization`)
                break
            case `clearPersonDetails`:
                await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.people })
                break
        }
    })

    const mapItems = (item: NavMainItem): NavMainItem => {
        if (item.route?.includes(`{linked_person_id}`)) {
            if (linkedPersonId.value !== null) {
                item.route = replace(item.route, `{linked_person_id}`, linkedPersonId.value.toString())
            } else {
                // Empty routes are filtered out
                item.route = ``
            }
        }

        if (me.hasBreezeBundle.value && item.key === `giving`) {
            item.route = `#tithely-giving`
            item.action = `tithely-giving`
        }

        return item
    }

    const nav = computed(() => {
        if (path === `/r/suspended`) {
            return []
        }

        const items = map(NAV_MAIN, mapItems)

        return filterNavPermissions(items, me)
    })

    const mainNavItems = computed(
        () =>
            map(me.data.value?.mainNav?.filter(item => item.secondary !== true) ?? [], item => ({
                ...item,
                route: item.redirect ?? item.route,
                secondary: item.secondary ?? undefined
            })) as NavMainItem[]
    )

    const secondaryNavItems = computed(
        () =>
            map(me.data.value?.mainNav?.filter(item => item.secondary === true) ?? [], item => ({
                ...item,
                route: item.redirect ?? item.route,
                secondary: item.secondary ?? undefined
            })) as NavMainItem[]
    )

    const hasGearMenuNav = computed(() => !me.data.value?.mainNavMiscConfig?.hideGearMenu)
    const hasOrgMenuNav = computed(() => !me.data.value?.mainNavMiscConfig?.hideOrganizationSettings)
    const homeNav = computed(() => me.data.value?.homeNav)

    return { nav, mainNavItems, secondaryNavItems, hasGearMenuNav, hasOrgMenuNav, homeNav }
}

export default useNavMain
